// src/components/Transaction/TransactionList.js - Mobile-First Enhanced Transaction Interface
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Divider,
  useMediaQuery,
  Drawer,
  useTheme,
  Badge,
  Menu,
  InputAdornment,
  alpha,
  Avatar,
  Paper,
  Fade
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  MoreVert as MoreVertIcon,
  SwapVert as TransactionIcon,
  AccountBalance as WalletIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/helpers';
import TransactionForm from './TransactionForm';
import ReceiptCapture from '../Receipts/ReceiptCapture';
import ReceiptProcessingScreen from '../Receipts/ReceiptProcessingScreen';
import { useCurrency } from '../../contexts/CurrencyContext';

export const TransactionList = ({ 
  transactions = [], 
  categories = [], 
  onSaveTransaction, 
  onDeleteTransaction,
  title,
  limit,
  showViewAll = false 
}) => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = useMediaQuery('(max-width:480px)');
  
  // Enhanced mobile-first state management
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showReceiptProcessing, setShowReceiptProcessing] = useState(false);
  const [receiptFormData, setReceiptFormData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isProcessingReceipt, setIsProcessingReceipt] = useState(false);
  const [showReceiptScanner, setShowReceiptScanner] = useState(false);

  // Detect filter changes
  useEffect(() => {
    const hasFilters = filterType !== 'all' || searchTerm !== '';
    setFiltersApplied(hasFilters);
  }, [filterType, searchTerm]);

  // Filter and sort transactions
  const filteredTransactions = transactions
    .filter(transaction => {
      if (filterType !== 'all' && transaction.type !== filterType) return false;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const category = categories.find(cat => cat.id === transaction.categoryId);
        return (
          transaction.description.toLowerCase().includes(searchLower) ||
          (category?.name.toLowerCase().includes(searchLower) || false)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
      }
      if (sortBy === 'amount') {
        return sortDirection === 'desc' ? b.amount - a.amount : a.amount - b.amount;
      }
      return 0;
    });

  const displayedTransactions = limit ? filteredTransactions.slice(0, limit) : filteredTransactions;

  // Calculate totals
  const totals = filteredTransactions.reduce(
    (acc, transaction) => {
      if (transaction.type === 'income') {
        acc.income += transaction.amount;
      } else {
        acc.expenses += transaction.amount;
      }
      return acc;
    },
    { income: 0, expenses: 0 }
  );
  
  // Group transactions by date
  const groupedTransactions = displayedTransactions.reduce((groups, transaction) => {
    const date = new Date(transaction.date).toISOString().split('T')[0]; // Use ISO format (YYYY-MM-DD) for consistent parsing
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(transaction);
    return groups;
  }, {});

  // Get category color
  const getCategoryColor = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.color || theme.palette.primary.main;
  };

  // Handlers
  const handleSave = (transactionData) => {
    onSaveTransaction(transactionData);
    setDialogOpen(false);
    setEditingTransaction(null);
  };

  const handleAddTransaction = () => {
    setEditingTransaction(null);
    setDialogOpen(true);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortDirection('desc');
    }
    handleSortClose();
  };

  const handleActionMenuOpen = (event, transactionId) => {
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedTransactionId(transactionId);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setSelectedTransactionId(null);
  };

  const handleReceiptCaptured = (receiptData) => {
    if (isProcessingReceipt) return;
    setShowReceiptScanner(false);
    setReceiptFormData(receiptData);
    setShowReceiptProcessing(true);
  };

  const handleReceiptProcessingComplete = (receiptDetails) => {
    setShowReceiptProcessing(false);
    setReceiptFormData(null);
  };

  const handleReceiptProcessingError = () => {
    setShowReceiptProcessing(false);
    setReceiptFormData(null);
  };

  // Conditional rendering for receipt screens
  if (showReceiptScanner) {
    return (
      <ReceiptCapture 
        onReceiptCaptured={handleReceiptCaptured}
        onCancel={() => setShowReceiptScanner(false)}
      />
    );
  }
  
  if (showReceiptProcessing && receiptFormData) {
    return (
      <ReceiptProcessingScreen
        receiptData={receiptFormData}
        onComplete={handleReceiptProcessingComplete}
        onError={handleReceiptProcessingError}
      />
    );
  }

  return (
    <Fade in timeout={400}>
      <Box 
        sx={{ 
          width: '100%',
          maxWidth: isMobile ? '100%' : '95%', 
          mx: 'auto', 
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 1, sm: 2, md: 3 },
          minHeight: '100vh',
          background: isMobile 
            ? theme.palette.mode === 'dark'
              ? '#0a0a0a'
              : '#f8fafc'
            : 'transparent'
        }}
      >
        {/* Compact Header */}
        <Paper
          elevation={isMobile ? 0 : 1}
          sx={{
            borderRadius: isMobile ? 0 : 2,
            p: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 2, sm: 2.5, md: 3 },
            background: theme.palette.background.paper,
            borderBottom: isMobile 
              ? `1px solid ${alpha(theme.palette.divider, 0.1)}`
              : 'none'
          }}
        >
          {/* Title Row */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5
          }}>
            <Box>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                fontWeight="700" 
                sx={{ 
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                  mb: 0.5
                }}
              >
                {title || t('transactions.title')} 
                <Typography 
                  component="span" 
                  sx={{ 
                    ml: 1,
                    color: theme.palette.text.secondary,
                    fontSize: '0.85em',
                    fontWeight: 500
                  }}
                >
                  ({displayedTransactions.length})
                </Typography>
              </Typography>
            </Box>
            
            {/* Action Buttons - Desktop */}
            {!limit && !isMobile && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <IconButton
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{
                    bgcolor: filtersApplied ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.15)
                    }
                  }}
                >
                  <Badge 
                    variant="dot" 
                    color="error" 
                    invisible={!filtersApplied}
                  >
                    <FilterIcon />
                  </Badge>
                </IconButton>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddTransaction}
                  sx={{
                    borderRadius: 2,
                    px: 3
                  }}
                >
                  {t('transactions.add')}
                </Button>
              </Box>
            )}
          </Box>
          
          {/* Compact Summary Pills */}
          <Box sx={{ 
            display: 'flex', 
            gap: isCompact ? 1 : 1.5,
            flexWrap: 'wrap',
            mb: !limit ? 1.5 : 0
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: isCompact ? 1 : 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}>
              <IncomeIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
              <Typography 
                variant="body2" 
                fontWeight="600"
                sx={{ color: theme.palette.success.main, fontSize: isCompact ? '0.8rem' : '0.875rem' }}
              >
                {formatCurrency(totals.income, false, isCompact)}
              </Typography>
            </Box>
            
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: isCompact ? 1 : 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
            }}>
              <ExpenseIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
              <Typography 
                variant="body2" 
                fontWeight="600"
                sx={{ color: theme.palette.error.main, fontSize: isCompact ? '0.8rem' : '0.875rem' }}
              >
                {formatCurrency(totals.expenses, false, isCompact)}
              </Typography>
            </Box>
            
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: isCompact ? 1 : 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.text.primary, 0.05),
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
            }}>
              <WalletIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
              <Typography 
                variant="body2" 
                fontWeight="600"
                sx={{ 
                  color: totals.income - totals.expenses >= 0 
                    ? theme.palette.success.main 
                    : theme.palette.error.main,
                  fontSize: isCompact ? '0.8rem' : '0.875rem'
                }}
              >
                {formatCurrency(totals.income - totals.expenses, false, isCompact)}
              </Typography>
            </Box>
          </Box>
          
          {/* Search & Filter Bar */}
          {!limit && (
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              alignItems: 'center'
            }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('transactions.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              {isMobile && (
                <>
                  <IconButton
                    onClick={() => setFilterDrawerOpen(true)}
                    sx={{
                      bgcolor: filtersApplied ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.action.hover, 0.5),
                      minWidth: 44,
                      minHeight: 44,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.15)
                      }
                    }}
                  >
                    <Badge 
                      variant="dot" 
                      color="error" 
                      invisible={!filtersApplied}
                    >
                      <FilterIcon />
                    </Badge>
                  </IconButton>
                  <IconButton
                    onClick={handleAddTransaction}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      minWidth: 44,
                      minHeight: 44,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2)
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Paper>

        {/* Transaction List */}
        <Box>
          {displayedTransactions.length > 0 ? (
            <Box>
              {Object.entries(groupedTransactions)
                .sort(([a], [b]) => new Date(b) - new Date(a))
                .map(([date, dateTransactions]) => (
                  <Box key={date} sx={{ mb: 3 }}>
                    {/* Simple Sticky Date Header */}
                    <Box sx={{ 
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      display: 'flex', 
                      alignItems: 'center',
                      mb: 1.5,
                      py: 1,
                      bgcolor: theme.palette.background.default,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          fontSize: isCompact ? '0.8rem' : '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {new Date(date).toLocaleDateString(undefined, {
                          weekday: isCompact ? undefined : 'long',
                          month: 'short',
                          day: 'numeric',
                          year: isCompact ? undefined : 'numeric'
                        })}
                      </Typography>
                      <Divider 
                        sx={{ 
                          flex: 1, 
                          ml: 2,
                          borderColor: alpha(theme.palette.divider, 0.3)
                        }} 
                      />
                    </Box>

                    {/* Clean Transaction Cards */}
                    <Paper 
                      elevation={0}
                      sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.7),
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <List disablePadding>
                        {dateTransactions.map((transaction, idx) => {
                          const category = categories.find(cat => cat.id === transaction.categoryId);
                          const isIncome = transaction.type === 'income';
                          const categoryColor = getCategoryColor(transaction.categoryId);
                          
                          return (
                            <React.Fragment key={transaction.id}>
                              {idx > 0 && (
                                <Divider 
                                  component="li" 
                                  sx={{ 
                                    borderColor: alpha(theme.palette.divider, 0.08),
                                    ml: isCompact ? 6 : 7
                                  }}
                                />
                              )}
                              <ListItem
                                sx={{
                                  py: isCompact ? 1.5 : 2,
                                  px: isCompact ? 1.5 : 2,
                                  transition: 'all 0.2s ease',
                                  '&:hover': { 
                                    bgcolor: alpha(theme.palette.action.hover, 0.4),
                                  },
                                  cursor: 'pointer',
                                  minHeight: 44
                                }}
                                secondaryAction={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {!isMobile ? (
                                      <>
                                        <IconButton
                                          edge="end"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingTransaction(transaction);
                                            setDialogOpen(true);
                                          }}
                                          sx={{
                                            color: alpha(theme.palette.text.primary, 0.4),
                                            '&:hover': { 
                                              color: theme.palette.primary.main,
                                              bgcolor: alpha(theme.palette.primary.main, 0.08)
                                            },
                                            minWidth: 32,
                                            minHeight: 32
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        
                                        <IconButton
                                          edge="end"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteTransaction(transaction.id);
                                          }}
                                          sx={{
                                            color: alpha(theme.palette.text.primary, 0.4),
                                            '&:hover': { 
                                              color: theme.palette.error.main,
                                              bgcolor: alpha(theme.palette.error.main, 0.08) 
                                            },
                                            minWidth: 32,
                                            minHeight: 32
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleActionMenuOpen(e, transaction.id);
                                        }}
                                        sx={{
                                          color: alpha(theme.palette.text.primary, 0.4),
                                          '&:hover': { 
                                            color: theme.palette.primary.main,
                                            bgcolor: alpha(theme.palette.primary.main, 0.08)
                                          },
                                          minWidth: 32,
                                          minHeight: 32
                                        }}
                                      >
                                        <MoreVertIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>
                                }
                              >
                                <ListItemIcon 
                                  sx={{ 
                                    minWidth: isCompact ? 40 : 48
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: alpha(categoryColor, 0.12),
                                      color: categoryColor,
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    {isIncome ? (
                                      <IncomeIcon fontSize="small" />
                                    ) : (
                                      <ExpenseIcon fontSize="small" />
                                    )}
                                  </Avatar>
                                </ListItemIcon>
                                
                                <ListItemText
                                  primary={
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'space-between',
                                      gap: 1
                                    }}>
                                      <Typography
                                        variant="body1"
                                        fontWeight="600"
                                        sx={{ 
                                          color: theme.palette.text.primary,
                                          fontSize: isCompact ? '0.9rem' : '1rem',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {transaction.description}
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        fontWeight="700"
                                        sx={{
                                          color: isIncome ? theme.palette.success.main : theme.palette.error.main,
                                          fontSize: isCompact ? '0.95rem' : '1.1rem',
                                          flexShrink: 0
                                        }}
                                      >
                                        {isIncome ? '+' : '-'}{formatCurrency(transaction.amount, false, isCompact)}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      gap: 1,
                                      mt: 0.5,
                                      flexWrap: 'wrap'
                                    }}>
                                      {category && (
                                        <Chip
                                          label={category.name}
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(categoryColor, 0.1),
                                            color: categoryColor,
                                            height: '20px',
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            '& .MuiChip-label': { px: 0.75 }
                                          }}
                                        />
                                      )}
                                      {/* Show recurring template indicator */}
                                      {transaction.recurring && !transaction.isRecurringInstance && (
                                        <Chip
                                          label={isCompact ? '🔄' : '🔄 Recurring'}
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                                            color: theme.palette.primary.main,
                                            height: '20px',
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            '& .MuiChip-label': { px: 0.75 }
                                          }}
                                        />
                                      )}
                                      {/* Show auto-created instance indicator */}
                                      {transaction.isRecurringInstance && (
                                        <Chip
                                          label="Auto"
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(theme.palette.text.secondary, 0.08),
                                            color: theme.palette.text.secondary,
                                            height: '20px',
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            '& .MuiChip-label': { px: 0.75 }
                                          }}
                                        />
                                      )}
                                      {/* Show budget month indicator if different from transaction date */}
                                      {(() => {
                                        const transactionDate = new Date(transaction.date);
                                        const transactionMonth = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}`;
                                        if (transaction.budgetMonth && transaction.budgetMonth !== transactionMonth) {
                                          const budgetDate = new Date(transaction.budgetMonth + '-01');
                                          return (
                                            <Chip
                                              label={isCompact ? '💰' : `💰 ${budgetDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
                                              size="small"
                                              sx={{
                                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                                color: theme.palette.warning.main,
                                                height: '20px',
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                '& .MuiChip-label': { px: 0.75 }
                                              }}
                                            />
                                          );
                                        }
                                        return null;
                                      })()}
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: alpha(theme.palette.text.secondary, 0.7),
                                          fontSize: '0.7rem'
                                        }}
                                      >
                                        {new Date(transaction.date).toLocaleDateString(undefined, {
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Paper>
                  </Box>
                ))}
                
              {showViewAll && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    View All Transactions
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            // Clean Empty State
            <Box
              sx={{
                textAlign: 'center',
                py: { xs: 6, sm: 8 },
                px: { xs: 3, sm: 4 }
              }}
            >
              <Box
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 80, sm: 100 },
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <TransactionIcon 
                  sx={{ 
                    fontSize: { xs: 40, sm: 50 },
                    color: alpha(theme.palette.primary.main, 0.4)
                  }} 
                />
              </Box>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                fontWeight="600" 
                color="text.primary" 
                gutterBottom
              >
                {t('transactions.empty.title')}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 3, 
                  maxWidth: 400, 
                  mx: 'auto'
                }}
              >
                {t('transactions.empty.description')}
              </Typography>
              {!limit && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleAddTransaction}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  {t('transactions.addFirst')}
                </Button>
              )}
            </Box>
          )}
        </Box>
        {/* Filter Drawer - Mobile */}
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
            }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight="600">
                Filter & Sort
              </Typography>
              <IconButton onClick={() => setFilterDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('transactions.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label={t('transactions.filters.type')}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">{t('transactions.filters.allTypes')}</MenuItem>
                  <MenuItem value="income">{t('transactions.filters.income')}</MenuItem>
                  <MenuItem value="expense">{t('transactions.filters.expense')}</MenuItem>
                </TextField>
              
                <TextField
                  fullWidth
                  select
                  size="small"
                  label={t('transactions.sorting.sortBy')}
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                >
                  <MenuItem value="date">{t('transactions.sorting.date')}</MenuItem>
                  <MenuItem value="amount">{t('transactions.sorting.amount')}</MenuItem>
                </TextField>
              </Box>
              
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setFilterType('all');
                  setSearchTerm('');
                  setSortBy('date');
                  setSortDirection('desc');
                }}
              >
                {t('transactions.filters.clearFilters')}
              </Button>
            </Box>
          </Box>
        </Drawer>

        {/* Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
        >
          <MenuItem onClick={() => handleSortChange('date')}>
            {t('transactions.sorting.sortByDate')}
          </MenuItem>
          <MenuItem onClick={() => handleSortChange('amount')}>
            {t('transactions.sorting.sortByAmount')}
          </MenuItem>
        </Menu>

        {/* Action Menu - Mobile */}
        <Menu
          anchorEl={actionMenuAnchorEl}
          open={Boolean(actionMenuAnchorEl)}
          onClose={handleActionMenuClose}
        >
          <MenuItem
            onClick={() => {
              const transaction = transactions.find(t => t.id === selectedTransactionId);
              if (transaction) {
                setEditingTransaction(transaction);
                setDialogOpen(true);
              }
              handleActionMenuClose();
            }}
          >
            <EditIcon sx={{ mr: 2 }} />
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedTransactionId) {
                onDeleteTransaction(selectedTransactionId);
              }
              handleActionMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 2 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Transaction Form Dialog */}
        {dialogOpen && (
          <TransactionForm
            open={dialogOpen}
            initialData={editingTransaction}
            categories={categories}
            onSave={handleSave}
            onClose={() => {
              setDialogOpen(false);
              setEditingTransaction(null);
            }}
            onCancel={() => {
              setDialogOpen(false);
              setEditingTransaction(null);
            }}
          />
        )}
      </Box>
    </Fade>
  );
};

export default TransactionList;
